// ─── E3.2: LV-Tabellen-Parser (XLSX/XLS/CSV, reine Logik, keine UI) ──────────
//
// Parst LV-Tabellen komplett IM BROWSER (SheetJS via CDN, xlsxLoader).
// Gibt EXAKT die Ergebnis-Shape von parseLvPdf zurueck - dadurch laufen
// Vorschau, Warnungen, Dubletten-Check und bulk-import-Payload in
// LvImport.js unveraendert (Vorschau bleibt Pflicht).
//
// Sicherheitsprinzip wie E3.1 (lieber Abbruch als stilles Falsch-Parsen):
//   1. Header-Zeile mit den Pflichtspalten (Bezeichnung/Kurztext, Menge,
//      EP, GP) nicht eindeutig gefunden -> LvParseError mit den erwarteten
//      Spaltennamen. Kein Raten, kein Mapping-UI (Entscheidung 21.07.2026).
//   2. Keine einzige vollstaendige Position -> Abbruch.
//   3. Mehr verworfene Datenzeilen als erkannte Positionen -> Abbruch.
//
// pos_nr ist im gesamten Stack optional (Schema nullable, bulk-import
// strChecked, POST/PUT || null - verifiziert 21.07.2026): Tabellen OHNE
// OZ-Spalte sind importierbar; statistik.hinweis macht das in der
// Vorschau transparent.
//
// Mehr-Sheet-Dateien: bewusst nur das ERSTE Sheet (Entscheidung
// 21.07.2026); statistik.weitereSheets traegt die Info fuer die Anzeige.

import { LvParseError, berechnePositionsWarnungen } from './lvParser';
import { loadXLSX } from './xlsxLoader';

// Synonyme je Zielfeld - Vergleich EXAKT auf normalisiertem Header
// (lowercase, Whitespace/Punkte/Klammern/Striche entfernt). Exakt statt
// substring, damit "Mengeneinheit" nicht als "Menge" fehlgreift.
const HEADER_SYNONYME = {
  pos_nr: ['oz', 'ordnungszahl', 'pos', 'posnr', 'position', 'positionsnummer', 'nr'],
  bezeichnung: ['kurztext', 'bezeichnung', 'beschreibung', 'text', 'leistung'],
  sollmenge: ['menge', 'sollmenge', 'anzahl'],
  einheit: ['einheit', 'me', 'einh', 'mengeneinheit'],
  einheitspreis: ['ep', 'einheitspreis', 'einzelpreis'],
  gesamtpreis: ['gp', 'gesamtpreis', 'gesamtbetrag'],
  langtext: ['langtext', 'volltext'],
  kategorie: ['kategorie', 'gewerk', 'titel'],
};
const PFLICHT_FELDER = ['bezeichnung', 'sollmenge', 'einheitspreis', 'gesamtpreis'];
const HEADER_SUCHZEILEN = 20;

// Normalisierung: lowercase + Whitespace/Punkte/Klammern/Striche UND
// typische Waehrungs-/Trennzeichen entfernen, damit "EP (€)", "GP €" oder
// "Gesamtpreis:" auf ihre Synonyme treffen.
const normHeader = (v) => String(v ?? '').toLowerCase().replace(/[\s.\-_/()[\]€$:,]+/g, '');

// Zahl aus Zelle: echte Zahlen (XLSX) direkt; Strings (CSV/Textzellen) mit
// de/en-Erkennung - bei Komma UND Punkt entscheidet das letzte Trennzeichen
// ("1.234,56" de / "1,234.56" en). Nur-Punkt wird als Dezimalpunkt gelesen
// (XLSX-Zahlen kommen ohnehin als number; der Grenzfall "1.234" als
// deutscher Tausender-String faellt in der Vorschau durch die
// GP-Plausibilitaets-Warnung auf). Nicht parsebar -> null.
export function parseZahl(v) {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (v == null) return null;
  let s = String(v).trim().replace(/[€\s]/g, '');
  if (s === '' || s === '-') return null;
  const iKomma = s.lastIndexOf(',');
  const iPunkt = s.lastIndexOf('.');
  if (iKomma >= 0 && iPunkt >= 0) {
    if (iKomma > iPunkt) s = s.replace(/\./g, '').replace(',', '.');
    else s = s.replace(/,/g, '');
  } else if (iKomma >= 0) {
    s = s.replace(/,/g, '.');
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

// Header-Zeile auf das Feld-Mapping pruefen. Rueckgabe:
//   { ok: true, map }                      - alle Pflichtfelder eindeutig
//   { ok: false, mehrdeutig: [feld, ...] } - Pflicht komplett, aber doppelt
//   { ok: false }                          - Pflicht nicht komplett
function mappeHeaderZeile(row) {
  const treffer = {};
  for (let col = 0; col < row.length; col++) {
    const norm = normHeader(row[col]);
    if (!norm) continue;
    for (const [feld, synonyme] of Object.entries(HEADER_SYNONYME)) {
      if (synonyme.includes(norm)) {
        if (!treffer[feld]) treffer[feld] = [];
        treffer[feld].push(col);
      }
    }
  }
  const pflichtKomplett = PFLICHT_FELDER.every((f) => (treffer[f] || []).length >= 1);
  if (!pflichtKomplett) return { ok: false };
  const mehrdeutig = PFLICHT_FELDER.filter((f) => treffer[f].length > 1);
  if (mehrdeutig.length > 0) return { ok: false, mehrdeutig };
  // Optionale Felder: bei Mehrfach-Treffern erste Spalte (weich, kein Abbruch).
  const map = {};
  for (const feld of Object.keys(HEADER_SYNONYME)) {
    map[feld] = treffer[feld] ? treffer[feld][0] : null;
  }
  return { ok: true, map };
}

const zellAuszug = (row) =>
  row.filter((c) => c != null && String(c).trim() !== '')
    .map((c) => String(c).trim()).join(' | ').slice(0, 150);

// Kern-Parser auf Array-of-Arrays - PURE Funktion ohne SheetJS/Browser,
// dadurch in Node testbar. parseLvTabelle (unten) ist der duenne Wrapper,
// der die Datei via SheetJS in genau dieses AoA verwandelt.
export function parseLvTabelleAusAoa(aoa, dateiname, sheetInfo = {}) {
  if (!Array.isArray(aoa) || aoa.length === 0) {
    throw new LvParseError('Das Tabellenblatt ist leer - keine Position gefunden. Import abgebrochen.');
  }

  // ── Header suchen ──
  let headerIdx = -1;
  let mapping = null;
  let mehrdeutigInfo = null;
  for (let i = 0; i < Math.min(aoa.length, HEADER_SUCHZEILEN); i++) {
    const k = mappeHeaderZeile(aoa[i] || []);
    if (k.ok) { headerIdx = i; mapping = k.map; break; }
    if (k.mehrdeutig) mehrdeutigInfo = { zeile: i + 1, felder: k.mehrdeutig };
  }
  if (headerIdx < 0) {
    if (mehrdeutigInfo) {
      throw new LvParseError(
        `Spaltenkopf mehrdeutig (Zeile ${mehrdeutigInfo.zeile}): ` +
        `${mehrdeutigInfo.felder.join(', ')} mehrfach vorhanden. ` +
        'Import abgebrochen - bitte doppelte Spalten in der Datei entfernen.'
      );
    }
    throw new LvParseError(
      'Spaltenkopf nicht erkannt: erwartet wird eine Kopfzeile mit Spalten wie ' +
      '"Bezeichnung/Kurztext", "Menge", "EP/Einheitspreis" und "GP/Gesamtpreis" ' +
      '(optional "OZ/Pos", "Einheit/ME", "Langtext", "Kategorie"). ' +
      'Import abgebrochen - es wurde nichts uebernommen.'
    );
  }

  // ── Datenzeilen ──
  const positionen = [];
  const uebersprungen = [];
  let verworfen = 0;
  const get = (row, feld) => (mapping[feld] != null ? row[mapping[feld]] : null);

  for (let i = headerIdx + 1; i < aoa.length; i++) {
    const row = aoa[i] || [];
    const zeileNr = i + 1; // 1-basiert wie in Excel angezeigt
    if (row.every((c) => c == null || String(c).trim() === '')) continue;

    const bezRaw = get(row, 'bezeichnung');
    const bezeichnung = bezRaw == null ? '' : String(bezRaw).trim();
    const menge = parseZahl(get(row, 'sollmenge'));
    const ep = parseZahl(get(row, 'einheitspreis'));
    const gp = parseZahl(get(row, 'gesamtpreis'));

    // Summen-/Titelzeile: GP vorhanden, aber keine Menge.
    if (menge == null && gp != null) {
      uebersprungen.push({ typ: 'summe', seite: zeileNr, zeilen: 1, auszug: zellAuszug(row) });
      continue;
    }
    if (!bezeichnung || menge == null || ep == null || gp == null) {
      verworfen += 1;
      uebersprungen.push({ typ: 'unbekannt', seite: zeileNr, zeilen: 1, auszug: zellAuszug(row) });
      continue;
    }

    const posNrRaw = get(row, 'pos_nr');
    const pos_nr = posNrRaw == null ? '' : String(posNrRaw).trim();
    const ltRaw = get(row, 'langtext');
    const katRaw = get(row, 'kategorie');
    const einheitRaw = get(row, 'einheit');
    positionen.push({
      pos_nr,
      pos_nr_original: pos_nr,
      bezeichnung,
      langtext: ltRaw == null ? '' : String(ltRaw).trim(),
      kategorie: katRaw == null ? null : (String(katRaw).trim() || null),
      einheit: einheitRaw == null ? '' : String(einheitRaw).trim(),
      sollmenge: menge,
      einheitspreis: ep,
      gesamtpreis: gp,
      seite: zeileNr,
    });
  }

  // ── Konfidenz-Waechter analog PDF ──
  if (positionen.length === 0) {
    throw new LvParseError(
      'Keine vollstaendige Position (Bezeichnung + Menge/EP/GP) gefunden. ' +
      'Import abgebrochen - es wurde nichts uebernommen.'
    );
  }
  if (verworfen > positionen.length) {
    throw new LvParseError(
      `Tabellen-Layout unsicher: ${verworfen} nicht zuordenbare Zeilen bei nur ` +
      `${positionen.length} erkannten Positionen. Import abgebrochen - ` +
      'es wurde nichts uebernommen.'
    );
  }

  berechnePositionsWarnungen(positionen);

  return {
    dateiname,
    // Tabellen tragen keine verlaesslichen Kopfdaten - Felder bleiben leer,
    // die Baustellen-Angaben werden wie gewohnt in der Vorschau gepflegt.
    kopf: { name: '', adresse: '', plz: '', ort: '', auftraggeber: '', projektnummer: '', lvTitel: '', datum: '' },
    positionen,
    uebersprungen,
    statistik: {
      zeilen: aoa.length,
      positionen: positionen.length,
      ozKandidaten: positionen.length,
      verworfen,
      uebersprungeneBloecke: uebersprungen.length,
      summeGp: positionen.reduce((s, p) => s + p.gesamtpreis, 0),
      sheet: sheetInfo.sheet || null,
      weitereSheets: sheetInfo.weitereSheets || 0,
      // Transparenz: pos_nr ist im Stack optional - ohne OZ-Spalte wird
      // trotzdem importiert, aber sichtbar gemacht (Vorschau, E3.2.2).
      hinweis: mapping.pos_nr == null
        ? 'Keine Positionsnummern-Spalte erkannt - Positionen werden ohne Pos.-Nr. importiert.'
        : null,
    },
  };
}

// CSV bewusst NICHT ueber SheetJS: dessen CSV-Reader coerct Zahlen mit
// locale-blinder Heuristik (Komma als Tausender) BEVOR parseZahl die
// Original-Strings sieht - "12,5" wuerde zu 125, "1.234,56" zu 1.23456.
// Also CSV als Text selbst tokenisieren (RFC-4180-nah: Quotes, doppelte
// Quotes, Delimiter/Newlines in Quotes), damit die de/en-Dezimalerkennung
// in parseZahl greift. XLSX/XLS liefern echte Zahl-Zellen -> weiter SheetJS.
export function parseCsvText(text) {
  let s = String(text || '');
  if (s.charCodeAt(0) === 0xFEFF) s = s.slice(1); // BOM
  // Delimiter ueber die ersten nicht-leeren Zeilen SUMMIEREN (nicht nur die
  // erste): eine delimiterlose Titelzeile oben wuerde sonst den Default
  // erzwingen. Zeilen ohne Delimiter tragen 0 bei und bestimmen nichts.
  // Semikolon dominiert deutsche CSVs auch bei Dezimal-Kommas, weil jede
  // Feldgrenze ein Semikolon ist (Header ohnehin komma-frei).
  const kopfZeilen = s.split(/\r\n|\r|\n/).filter((l) => l.trim() !== '').slice(0, HEADER_SUCHZEILEN);
  const summe = (ch) => kopfZeilen.reduce((n, l) => n + (l.split(ch).length - 1), 0);
  const semi = summe(';'), tab = summe('\t'), komma = summe(',');
  const delim = (semi >= tab && semi >= komma && semi > 0) ? ';'
    : (tab >= komma && tab > 0) ? '\t' : ',';

  const rows = [];
  let feld = '', zeile = [], inQuote = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuote) {
      if (c === '"') {
        if (s[i + 1] === '"') { feld += '"'; i++; } else inQuote = false;
      } else feld += c;
    } else if (c === '"') {
      inQuote = true;
    } else if (c === delim) {
      zeile.push(feld); feld = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && s[i + 1] === '\n') i++;
      zeile.push(feld); rows.push(zeile); feld = ''; zeile = [];
    } else feld += c;
  }
  if (feld !== '' || zeile.length > 0) { zeile.push(feld); rows.push(zeile); }
  return rows;
}

// Datei-Wrapper: XLSX/XLS via SheetJS, CSV als Text. Beide -> Kern-Parser.
export async function parseLvTabelle(file) {
  const endung = (file.name.split('.').pop() || '').toLowerCase();
  if (endung === 'csv') {
    let text;
    try { text = await file.text(); }
    catch { throw new LvParseError('CSV-Datei konnte nicht gelesen werden.'); }
    return parseLvTabelleAusAoa(parseCsvText(text), file.name, { sheet: null, weitereSheets: 0 });
  }
  let XLSX;
  try {
    XLSX = await loadXLSX();
  } catch {
    throw new LvParseError('Excel-Bibliothek konnte nicht geladen werden (offline oder CDN blockiert?).');
  }
  let wb;
  try {
    const buf = await file.arrayBuffer();
    wb = XLSX.read(buf, { type: 'array' });
  } catch {
    throw new LvParseError('Datei konnte nicht gelesen werden - kein gueltiges Excel-Format?');
  }
  const sheetName = wb.SheetNames && wb.SheetNames[0];
  if (!sheetName) {
    throw new LvParseError('Die Datei enthaelt kein Tabellenblatt. Import abgebrochen.');
  }
  const aoa = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, raw: true, defval: null });
  return parseLvTabelleAusAoa(aoa, file.name, {
    sheet: sheetName,
    weitereSheets: wb.SheetNames.length - 1,
  });
}
